import React, { useState, useEffect } from 'react';
import './Temporadas.css';
import { crearJugadorReal } from '../../services/jugadoresService';
import { getClubJugadores } from '../../services/clubesService';
import ModalEditarJugador from './ModalEditarJugador';

const MAX_JUGADORES = { 'DOBLES MIXTO': 4, default: 3 };
const MODALIDADES_DOBLES = ['DOBLES FEMENINO', 'DOBLES MASCULINO', 'DOBLES MIXTO'];

// No hay titular/suplente en la base de datos: todos los inscritos son
// jugadores de pleno derecho (ver DOCUMENTACION_BD.md §7). Solo se valida
// cuántos hay y de qué género.
function validarComposicion(jugadores, modalidad) {
  const errs = [];
  const max = MAX_JUGADORES[modalidad] ?? MAX_JUGADORES.default;
  if (jugadores.length < 2) errs.push('Se necesitan al menos 2 jugadores.');
  if (jugadores.length > max) errs.push(`Máximo ${max} jugadores para esta modalidad.`);
  if (modalidad === 'DOBLES MIXTO') {
    const h = jugadores.filter(j => j.genero === 'M').length;
    const m = jugadores.filter(j => j.genero === 'F').length;
    if (h < 1 || h > 2) errs.push('Dobles Mixto: debe haber entre 1 y 2 hombres.');
    if (m < 1 || m > 2) errs.push('Dobles Mixto: debe haber entre 1 y 2 mujeres.');
  }
  if (modalidad === 'DOBLES FEMENINO' && jugadores.some(j => j.genero === 'M'))
    errs.push('Dobles Femenino: solo jugadoras (F).');
  if (modalidad === 'DOBLES MASCULINO' && jugadores.some(j => j.genero === 'F'))
    errs.push('Dobles Masculino: solo jugadores (M).');
  return errs;
}

export default function ModalEquipo({ temporada, modalidad, ocupados = [], equipo, onGuardar, onCerrar, onJugadorRenombrado }) {
  const editando = !!equipo;
  const esDobles = MODALIDADES_DOBLES.includes(modalidad);
  const [nombre, setNombre] = useState(equipo?.nombre_mostrar ?? '');
  const [jugadores, setJugadores] = useState(
    equipo?.jugadores?.map(j => ({ id: j.jugadorId, nombre: j.nombre, genero: j.genero }))
      ?? [{ id: '', nombre: '', genero: 'F' }]
  );
  const [todosJugadores, setTodosJugadores] = useState([]);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoGenero, setNuevoGenero] = useState('F');
  const [errores, setErrores] = useState([]);
  const [editandoJugador, setEditandoJugador] = useState(null);

  useEffect(() => {
    // Solo los jugadores inscritos en un club de esta temporada (pestaña "Por club").
    getClubJugadores(temporada).then(filas => {
      const vistos = new Map();
      filas.forEach(f => vistos.set(Number(f.jugador_id), {
        id: Number(f.jugador_id), nombre: f.nombre, alias: f.alias ?? null, genero: f.genero,
      }));
      setTodosJugadores([...vistos.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    }).catch(err => setErrores([err.message || 'No se pudo cargar la plantilla de la temporada.']));
  }, [temporada]);

  const cambiarJugador = (i, valor) => {
    const arr = [...jugadores];
    arr[i] = { ...arr[i], id: valor };
    const j = todosJugadores.find(j => String(j.id) === String(valor));
    if (j) { arr[i].nombre = j.nombre; arr[i].genero = j.genero; }
    setJugadores(arr);
  };

  const agregarFila = () => setJugadores(j => [...j, { id: '', nombre: '', genero: 'F' }]);
  const quitarFila = (i) => setJugadores(j => j.filter((_, idx) => idx !== i));

  const crearYAgregar = async () => {
    if (!nuevoNombre.trim()) return;
    const creado = await crearJugadorReal({ nombre: nuevoNombre.trim(), genero: nuevoGenero });
    setTodosJugadores(prev => [...prev, creado].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    setJugadores(prev => [...prev, { id: creado.id, nombre: creado.nombre, genero: creado.genero }]);
    setNuevoNombre(''); setNuevoGenero('F');
  };

  // Para individual: el nombre del equipo es automáticamente el nombre del jugador
  const seleccionarJugadorIndividual = (idSeleccionado) => {
    const j = todosJugadores.find(j => String(j.id) === String(idSeleccionado));
    if (j) {
      setJugadores([{ id: j.id, nombre: j.nombre, genero: j.genero }]);
      setNombre(j.nombre);
    } else {
      setJugadores([{ id: '', nombre: '', genero: 'F' }]);
      setNombre('');
    }
  };

  const crearJugadorIndividual = async () => {
    if (!nuevoNombre.trim()) return;
    const creado = await crearJugadorReal({ nombre: nuevoNombre.trim(), genero: nuevoGenero });
    setTodosJugadores(prev => [...prev, creado].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')));
    seleccionarJugadorIndividual(creado.id);
    setNuevoNombre(''); setNuevoGenero('F');
  };

  // Corrige el nombre y el alias del jugador en el catálogo global (p.ej. un
  // typo al darlo de alta): no crea uno nuevo ni toca su inscripción. Se
  // guarda al momento, independiente del "Guardar" del equipo.
  const empezarEdicion = (j) => {
    const completo = todosJugadores.find(t => String(t.id) === String(j.id));
    setEditandoJugador({ id: j.id, nombre: completo?.nombre ?? j.nombre, alias: completo?.alias ?? null });
  };

  const jugadorEditado = async ({ id, nombre: nombreCorregido, alias }) => {
    const mismo = (j) => String(j.id) === String(id);
    setTodosJugadores(prev =>
      prev.map(j => (mismo(j) ? { ...j, nombre: nombreCorregido, alias } : j))
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
    );
    setJugadores(prev => prev.map(j => (mismo(j) ? { ...j, nombre: nombreCorregido } : j)));
    if (!esDobles) setNombre(nombreCorregido);
    await onJugadorRenombrado?.(id, nombreCorregido);
  };

  const guardar = () => {
    const errs = esDobles ? validarComposicion(jugadores, modalidad) : [];
    if (!esDobles && !jugadores[0]?.id) errs.push('Selecciona un jugador.');
    if (esDobles && !nombre.trim()) errs.unshift('El nombre del equipo es obligatorio.');
    if (errs.length) { setErrores(errs); return; }
    onGuardar({ equipo: esDobles ? nombre.trim() : jugadores[0].nombre, jugadores, modalidad });
  };

  const tituloAccion = esDobles
    ? (editando ? 'Editar equipo' : 'Añadir equipo')
    : (editando ? 'Editar jugador' : 'Añadir jugador');

  const generoFiltro = modalidad.includes('FEMENINO') ? 'F'
    : modalidad.includes('MASCULINO') ? 'M'
    : null;
  // Fuera del selector: quien ya está en otro equipo de esta modalidad
  // (`ocupados`) y quien ya está elegido en otra fila de este mismo modal.
  const ocupadosSet = new Set(ocupados.map(String));
  const opcionesPara = (idActual) => {
    const elegidos = new Set(jugadores.map(j => String(j.id)).filter(id => id && id !== String(idActual)));
    return todosJugadores.filter(j =>
      (!generoFiltro || j.genero === generoFiltro)
      && (String(j.id) === String(idActual) || (!ocupadosSet.has(String(j.id)) && !elegidos.has(String(j.id))))
    );
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box">
        <h3>{tituloAccion} — {modalidad}</h3>

        {esDobles && (
          <div className="form-row">
            <label>Nombre del equipo</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: CDC ALTATORRE A" />
          </div>
        )}

        {esDobles && (
          <div className="jugadores-editor">
            <div className="jugadores-editor-header">
              <span>Jugadores</span>
              <button className="btn btn-secondary" style={{ fontSize: '.75rem', padding: '2px 8px' }} onClick={agregarFila}>+ Fila</button>
            </div>
            {jugadores.map((j, i) => (
              <div key={i} className="jugador-row">
                <>
                    <select
                      style={{ flex: 1 }}
                      value={j.id}
                      onChange={e => cambiarJugador(i, e.target.value)}
                    >
                      <option value="">Seleccionar jugador</option>
                      {opcionesPara(j.id).map(jj => (
                        <option key={jj.id} value={jj.id}>{jj.nombre} ({jj.genero})</option>
                      ))}
                    </select>
                    {j.id && (
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '.75rem', padding: '2px 6px' }}
                        title="Editar nombre y alias de este jugador"
                        onClick={() => empezarEdicion(j)}
                      >
                        ✏️
                      </button>
                    )}
                    <button className="remove" onClick={() => quitarFila(i)}>✕</button>
                </>
              </div>
            ))}
            <div className="new-player-form">
              <input placeholder="Nuevo jugador..." value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} />
              <select value={nuevoGenero} onChange={e => setNuevoGenero(e.target.value)}>
                <option value="F">F</option>
                <option value="M">M</option>
              </select>
              <button className="btn btn-secondary" style={{ fontSize: '.78rem' }} onClick={crearYAgregar}>+ Crear y añadir</button>
            </div>
          </div>
        )}

        {!esDobles && (
          <div className="jugadores-editor">
            <div className="jugadores-editor-header"><span>Jugador</span></div>
            <div className="jugador-row">
              <>
                  <select
                    style={{ flex: 1 }}
                    value={jugadores[0]?.id ?? ''}
                    onChange={e => seleccionarJugadorIndividual(e.target.value)}
                  >
                    <option value="">Seleccionar jugador</option>
                    {opcionesPara(jugadores[0]?.id).map(jj => (
                      <option key={jj.id} value={jj.id}>{jj.nombre} ({jj.genero})</option>
                    ))}
                  </select>
                  {jugadores[0]?.id && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '.75rem', padding: '2px 6px' }}
                      title="Editar nombre y alias de este jugador"
                      onClick={() => empezarEdicion(jugadores[0])}
                    >
                      ✏️
                    </button>
                  )}
                </>
            </div>
            <div className="new-player-form">
              <input
                placeholder="Nuevo jugador..."
                value={nuevoNombre}
                onChange={e => setNuevoNombre(e.target.value)}
              />
              <select value={nuevoGenero} onChange={e => setNuevoGenero(e.target.value)}>
                <option value="F">F</option>
                <option value="M">M</option>
              </select>
              <button
                className="btn btn-secondary"
                style={{ fontSize: '.78rem' }}
                onClick={crearJugadorIndividual}
              >
                + Crear y seleccionar
              </button>
            </div>
          </div>
        )}

        {errores.length > 0 && (
          <ul style={{ color: '#c0392b', fontSize: '.83rem', paddingLeft: '1.2rem', marginBottom: '.8rem' }}>
            {errores.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar}>Guardar</button>
        </div>
      </div>

      {editandoJugador && (
        <ModalEditarJugador
          jugador={editandoJugador}
          onGuardado={jugadorEditado}
          onCerrar={() => setEditandoJugador(null)}
        />
      )}
    </div>
  );
}
